import React, { FC, ReactNode, ReactElement, useMemo } from 'react';
import MuiBox, { BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';
import Stack from '@mui/material/Stack';
import Tabs from 'src/components/Tabs';
import Button, { ButtonVariantType } from '../Button';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import ActionsMenu from '../ActionsMenu';
import { SxProps, Theme, useTheme } from '@mui/material/styles';

const Box = styled(MuiBox)<BoxProps>(({ theme }) => ({
    display: 'flow-root',
    padding: 0,
    position: 'sticky',
    background: colors.background_W,
    zIndex: 1000,
    top: `-${sizes.body_PD}`,
    marginTop: `-${sizes.body_PD}`,
    paddingTop: sizes.body_PD,

    '& .MuiButton-root': {
        marginLeft: '8px',
        padding: '8px 16px',
        minWidth: 'unset',
        fontSize: '16px',

        '&.MuiButton-outlined': {
            borderColor: colors.text_T3,
            padding: '8px 10px',
            color: colors.text_T1,

            '&:hover': {
                backgroundColor: colors.background_BQ,
            },

            '&.active': {
                borderColor: colors.primary_O,
                color: colors.primary_O,

                '& svg': {
                    fill: colors.primary_O,
                    '& path': {
                        fill: colors.primary_O,
                    },
                },
            },
        },

        svg: {
            width: '20px',
            height: '20px',
        },
    },
    '& > div > hr.MuiDivider-root': {
        marginLeft: '8px',
    },

    '& .body-top-block-menu': {
        marginLeft: 0,
    },

    '& .body-top-block-actions': {
        float: 'right',
        padding: sizes.body_top_PD,
    },

    '& .MuiTabs-root': {
        minHeight: 'unset',
        padding: sizes.body_top_PD,

        '& button': {
            minHeight: '38px',
            padding: '0 16px',
        },
    },

    [theme.breakpoints.down('sm')]: {
        paddingBottom: sizes.body_PD,
        marginTop: '-15px',
        '& .MuiTabs-root': {
            paddingBottom: 0,
        },
        '& .body-top-block-actions': {
            paddingTop: sizes.body_PD,
            paddingBottom: 0,
        },
    },
}));

export interface ActionType {
    onClick?: (e?: any) => void;
    icon?: ReactNode;
    label?: string;
    tooltip?: string;
    variant?: ButtonVariantType;
    hidden?: boolean;
    disabled?: boolean;
    node?: ReactElement;
    active?: boolean;
    divider?: boolean;
    items?: ActionType[];
    sx?: SxProps<Theme>;
    element?: ReactElement | null;
    /** Stable React key for custom elements (avoids remount when filtered action indices shift). */
    actionKey?: string;
    'data-testid'?: string;
}

export interface TabType {
    value: string;
    label: string;
    hidden?: boolean;
}

export interface ActionsPropsType {
    actions: ActionType[];
}

const renderAction = (action: ActionType, index: number, fullWidth?: boolean) => {
    const key = action.actionKey ?? index;
    if (action.divider) {
        return <Divider orientation={fullWidth ? 'horizontal' : 'vertical'} flexItem key={key}></Divider>;
    }
    if (action.node) {
        return <React.Fragment key={key}>{action.node}</React.Fragment>;
    }
    return (
        <Button
            onClick={action.onClick}
            key={key}
            variant={action.variant}
            tooltiop={action.tooltip}
            disabled={action.disabled}
            className={action.active ? 'active' : undefined}
            fullWidth={fullWidth}
            sx={action.sx}
            data-testid={action['data-testid']}
        >
            {action.icon}
            {action.label}
        </Button>
    );
};

const FormActions: FC<ActionsPropsType> = (props) => {
    const filteredActions: ActionType[] = useMemo(() => {
        let filteredActions = props.actions.filter((action) => !action.hidden);
        filteredActions = filteredActions.filter(
            (action, index) => !(action.divider && (!index || filteredActions[index - 1].divider))
        );
        return filteredActions;
    }, [props.actions]);

    return (
        <Stack spacing={2} direction="row" className="body-top-block-actions">
            {filteredActions.map((action, index) => {
                const key = action.actionKey ?? index;
                if (action.element !== undefined) {
                    if (action.element) {
                        return React.cloneElement(action.element, { key });
                    }
                    return null;
                }
                if (action.items) {
                    let items = action.items.filter((item) => !item.hidden);
                    items = items.filter(
                        (item, itemIndex) => !(item.divider && (!itemIndex || items[itemIndex - 1].divider))
                    );

                    if (!items.length) {
                        return null;
                    }
                    return <ActionsMenu actions={items} buttonVariant="outlined" key={key} />;
                }
                return renderAction(action, index);
            })}
        </Stack>
    );
};

export interface BodyTopBlockType {
    actions?: ActionType[];
    tabs?: TabType[];
    selectTab?: string;
    onChangeTab?: (value: string) => void;
    onClickTab?: (value: string) => void;
    disableTabs?: boolean;
}

const BodyTopBlock: FC<BodyTopBlockType> = (props) => {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    const tabs = useMemo(() => {
        if (props.tabs && props.selectTab !== undefined && props.onChangeTab)
            return (
                <Tabs
                    items={props.tabs.filter((tab) => !tab.hidden)}
                    selected={props.selectTab}
                    onChange={props.onChangeTab}
                    onClickTab={props.onClickTab}
                    disabled={props.disableTabs}
                />
            );
        return undefined;
    }, [props.tabs, props.selectTab, props.onChangeTab, props.onClickTab, props.disableTabs]);

    return (
        <Box className="body-top-block">
            {mobile && tabs}
            {props.actions && <FormActions actions={props.actions} />}
            {!mobile && tabs}
        </Box>
    );
};

export default BodyTopBlock;
